import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { FiAlertCircle, FiCheck, FiCopy, FiPlus, FiToggleLeft, FiToggleRight } from 'react-icons/fi';
import { authService, roomService, workflowService } from '../api/services';
import { newDraftId } from '../utils/depositDrafts';
import ConfirmDialog from '../components/ConfirmDialog';
import { useLanguage } from '../contexts/LanguageContext';

const MyRooms = ({ adminMode = false }) => {
  const { t, language } = useLanguage();
  const locale = language === 'en' ? 'en-US' : 'vi-VN';
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notice, setNotice] = useState('');
  const [renewal, setRenewal] = useState(null);
  const [renewing, setRenewing] = useState(false);
  const [releasing, setReleasing] = useState(null);
  const releaseRental = async () => {
    if (!releasing || renewing) return;
    setRenewing(true);
    try { const updated = await workflowService.release(releasing.id); setRooms((previous) => previous.map((room) => room.id === updated.id ? { ...room,...updated,reservation:null } : room)); setReleasing(null); setNotice('Đã kết thúc thuê và mở lại phòng. Lịch sử giao dịch và hoa hồng được giữ nguyên.'); }
    catch (error) { setNotice(error.response?.data?.message || 'Không thể kết thúc thuê.'); }
    finally { setRenewing(false); }
  };
  const prepareRenewal = async (room) => {
    setRenewing(true);
    try { const plan = await workflowService.settings(); setRenewal({ room, plan, key: newDraftId() }); }
    catch { setNotice('Không tải được phí gia hạn. Vui lòng thử lại.'); }
    finally { setRenewing(false); }
  };
  const renew = async () => {
    if (!renewal || renewing) return;
    setRenewing(true);
    try {
      const updated = await workflowService.renew(renewal.room.id, { request_key:renewal.key, quoted_price:renewal.plan.price, quoted_days:renewal.plan.duration_days, accept_fee:true });
      setRooms((previous) => previous.map((room) => room.id === updated.id ? { ...room,...updated } : room));
      setRenewal(null); setNotice('Đã gia hạn tin đăng thành công.');
    } catch (error) { setNotice(error.response?.data?.message || 'Gia hạn thất bại. Chưa có giao dịch thành công thì ví không bị trừ tiền.'); }
    finally { setRenewing(false); }
  };
  const [roomToDelete, setRoomToDelete] = useState(null);
  const [copiedCode, setCopiedCode] = useState('');
  const [copyError, setCopyError] = useState(false);
  const copyTimerRef = useRef(null);
  const [updatingRoomIds, setUpdatingRoomIds] = useState({});
  const pendingStatusRef = useRef(new Set());
  const [statusToast, setStatusToast] = useState(null);
  const statusTimerRef = useRef(null);
  const currentUser = authService.getCurrentUser();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({ total: 0, last_page: 1 });
  const [fetchError, setFetchError] = useState(false);
  const [reload, setReload] = useState(0);
  const en = language === 'en';
  const title = adminMode ? (en ? 'Manage all rooms' : 'Quản lý tất cả phòng') : t('myRooms.title');
  const statusLabels = en
    ? { available: 'Available', rented: 'Rented', soon_available: 'Available soon', transfer: 'Transfer', closed: 'Hidden', reserved: 'Reserved', maintenance: 'Maintenance' }
    : { available: 'Còn trống', rented: 'Đã cho thuê', soon_available: 'Sắp trống', transfer: 'Sang nhượng', closed: 'Đã ẩn', reserved: 'Đã đặt cọc', maintenance: 'Bảo trì' };

  useEffect(() => {
    if (!adminMode) return;
    const controller = new AbortController();
    setLoading(true);
    setFetchError(false);
    const timer = setTimeout(async () => {
      try {
        const result = await roomService.getAdminRooms({ search: search.trim(), status: statusFilter || undefined, page }, controller.signal);
        if (controller.signal.aborted) return;
        setRooms(result.data);
        setPagination({ total: result.total, last_page: result.last_page });
        if (page > result.last_page) setPage(result.last_page);
      } catch {
        if (!controller.signal.aborted) setFetchError(true);
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    }, 300);
    return () => { clearTimeout(timer); controller.abort(); };
  }, [adminMode, search, statusFilter, page, reload]);

  useEffect(() => () => { clearTimeout(copyTimerRef.current); clearTimeout(statusTimerRef.current); }, []);

  const canManageRoom = (room) => Boolean(currentUser && (currentUser.role === 'admin' || Number(currentUser.id) === Number(room.owner_id)));

  const toggleRoomStatus = async (room) => {
    if (!canManageRoom(room) || pendingStatusRef.current.has(room.id)) return;
    if (room.status === 'rented' && room.reservation?.status === 'confirmed') { setReleasing(room); return; }
    pendingStatusRef.current.add(room.id);
    setUpdatingRoomIds((previous) => ({ ...previous, [room.id]: true }));
    clearTimeout(copyTimerRef.current);
    clearTimeout(statusTimerRef.current);
    setCopiedCode(''); setCopyError(false); setStatusToast(null);
    const status = room.status === 'available' ? 'rented' : 'available';
    try {
      const updated = await roomService.updateRoom(room.id, { status });
      setRooms((previous) => previous.map((item) => item.id === room.id ? { ...item, ...updated, status: updated.status || status } : item));
      setStatusToast({ error: false, text: t(status === 'available' ? 'myRooms.roomActivated' : 'myRooms.roomHidden') });
    } catch {
      setStatusToast({ error: true, text: t('myRooms.statusUpdateFailed') });
    } finally {
      pendingStatusRef.current.delete(room.id);
      setUpdatingRoomIds((previous) => ({ ...previous, [room.id]: false }));
      clearTimeout(statusTimerRef.current);
      statusTimerRef.current = setTimeout(() => setStatusToast(null), 3000);
    }
  };

  const copyRoomId = async (code) => {
    clearTimeout(statusTimerRef.current);
    setStatusToast(null);
    clearTimeout(copyTimerRef.current);
    setCopyError(false);
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(code);
      } else {
        const previousFocus = document.activeElement;
        const input = document.createElement('textarea');
        input.value = code;
        input.style.position = 'fixed';
        input.style.opacity = '0';
        document.body.appendChild(input);
        try {
          input.select();
          if (!document.execCommand('copy')) throw new Error('Copy failed');
        } finally {
          input.remove();
          previousFocus?.focus();
        }
      }
      setCopiedCode(code);
    } catch {
      setCopiedCode('');
      setCopyError(true);
    } finally {
      clearTimeout(copyTimerRef.current);
      copyTimerRef.current = setTimeout(() => {
        setCopiedCode('');
        setCopyError(false);
      }, 3000);
    }
  };

  useEffect(() => {
    if (!adminMode) fetchMyRooms();

    const savedNotice = localStorage.getItem('myRoomsNotice');
    if (savedNotice) {
      setNotice(savedNotice);
      localStorage.removeItem('myRoomsNotice');
    }
  // The admin list has its own cancellable, paginated request above.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adminMode]);

  const fetchMyRooms = async () => {
    const extractRooms = (payload) => {
      if (Array.isArray(payload)) {
        return payload;
      }

      if (Array.isArray(payload?.data)) {
        return payload.data;
      }

      if (Array.isArray(payload?.data?.data)) {
        return payload.data.data;
      }

      return [];
    };

    try {
      const response = await roomService.getMyRooms();
      setRooms(extractRooms(response));
    } catch (error) {
      console.error('Error fetching rooms:', error);
      setRooms([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    const id = roomToDelete;
    if (!id) {
      return;
    }

    try {
      await roomService.deleteRoom(id);
      setRooms((prev) => prev.filter((room) => room.id !== id));
      setRoomToDelete(null);
      if (adminMode) setReload((value) => value + 1);
    } catch (error) {
      alert(t('myRooms.deleteFailed'));
    }
  };

  if (loading && !adminMode) {
    return (
      <div className="container">
        <div className="page-header">
          <h1 className="page-title">{t('myRooms.title')}</h1>
        </div>
        <div className="skeleton-table">
          {Array.from({ length: 5 }).map((_, index) => (
            <div key={`my-room-skeleton-${index}`} className="skeleton-table-row">
              <div className="skeleton-shimmer skeleton-line" />
              <div className="skeleton-shimmer skeleton-line" />
              <div className="skeleton-shimmer skeleton-line" />
              <div className="skeleton-shimmer skeleton-line sm" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="container">
      <div className="page-header">
        <h1 className="page-title">{title}</h1>
        <Link to="/create-room" className="btn btn-primary">
          + {t('myRooms.createNew')}
        </Link>
      </div>

      {adminMode && <>
        <p>{en ? 'All listings, including hidden rooms. Manage availability or edit any listing.' : 'Tất cả tin đăng, bao gồm phòng đã ẩn. Đổi trạng thái nhanh hoặc chỉnh sửa thông tin phòng.'}</p>
        <div className="admin-room-filters">
          <label>{en ? 'Search rooms' : 'Tìm kiếm phòng'}
            <input type="search" value={search} placeholder={en ? 'ID, title, address, owner name or email' : 'ID, tiêu đề, địa chỉ, tên hoặc email người đăng'} onChange={(event) => { setSearch(event.target.value); setPage(1); }} />
          </label>
          <label>{t('myRooms.colStatus')}
            <select value={statusFilter} onChange={(event) => { setStatusFilter(event.target.value); setPage(1); }}>
              <option value="">{en ? 'All statuses' : 'Tất cả trạng thái'}</option>
              {['available', 'rented'].map((value) => <option key={value} value={value}>{statusLabels[value]}</option>)}
            </select>
          </label>
        </div>
        {!loading && !fetchError && <p role="status">{pagination.total} {en ? 'rooms' : 'phòng'}</p>}
      </>}
      {adminMode && fetchError ? <div role="alert" className="error-message">
        {en ? 'Unable to load rooms.' : 'Không tải được danh sách phòng.'} <button type="button" className="btn" onClick={() => setReload((value) => value + 1)}>{en ? 'Retry' : 'Thử lại'}</button>
      </div> : adminMode && loading ? <p role="status">{en ? 'Loading rooms…' : 'Đang tải danh sách phòng…'}</p> : rooms.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon">🏠</div>
          <h3 className="empty-state-title">{adminMode ? (en ? 'No matching rooms' : 'Không có phòng phù hợp') : t('myRooms.emptyTitle')}</h3>
          <p className="empty-state-description">
            {adminMode ? (en ? 'Try another search or status.' : 'Thử từ khóa hoặc trạng thái khác.') : t('myRooms.emptyDescription')}
          </p>
          <div className="empty-state-actions">
            <Link to="/create-room" className="btn btn-primary" aria-label={t('myRooms.createNew')}>
              <FiPlus size={16} /> {t('myRooms.createFirst')}
            </Link>
          </div>
        </div>
      ) : (
        <>
        {notice && <div className="success-message">{notice}</div>}
        {statusToast && <div role="status" aria-live="polite" className={`room-copy-toast${statusToast.error ? ' room-copy-toast-error' : ''}`}>
          {statusToast.error ? <FiAlertCircle size={20} aria-hidden="true" /> : <FiCheck size={20} aria-hidden="true" />}
          <span>{statusToast.text}</span>
        </div>}
        {(copiedCode || copyError) && <div role="status" aria-live="polite" className={`room-copy-toast${copyError ? ' room-copy-toast-error' : ''}`}>
          {copyError ? <FiAlertCircle size={20} aria-hidden="true" /> : <FiCheck size={20} aria-hidden="true" />}
          <span>{copyError ? t('myRooms.copyIdFailed') : t('myRooms.idCopied')}</span>
        </div>}
        <div className={`table managed-rooms-table${adminMode ? ' managed-rooms-table-admin' : ''}`}>
          <table>
            <colgroup>
              <col style={{ width: '18%' }} />
              <col style={{ width: adminMode ? '10%' : '16%' }} />
              {adminMode && <col style={{ width: '17%' }} />}
              <col style={{ width: adminMode ? '25%' : '30%' }} />
              <col style={{ width: '10%' }} />
              <col style={{ width: adminMode ? '10%' : '13%' }} />
              <col style={{ width: adminMode ? '10%' : '13%' }} />
            </colgroup>
            <thead>
              <tr>
                <th>Mã phòng</th>
                <th>{t('myRooms.colTitle')}</th>
                {adminMode && <th>{en ? 'Posted by' : 'Người đăng'}</th>}
                <th>{t('myRooms.colAddress')}</th>
                <th>{t('myRooms.colPrice')}</th>
                <th>{t('myRooms.colStatus')}</th>
                <th>{t('myRooms.colActions')}</th>
              </tr>
            </thead>
            <tbody>
              {rooms.map((room) => (
                <tr key={room.id}>
                  <td>
                    <div className="my-room-code">
                      <strong>{room.listing_code || 'Đang cập nhật'}</strong>
                      {room.listing_code && <button
                        type="button"
                        className="my-room-copy-button"
                        onClick={() => copyRoomId(room.listing_code)}
                        title={t('myRooms.copyId')}
                        aria-label={`${t('myRooms.copyId')}: ${room.listing_code}`}
                      >
                        {copiedCode === room.listing_code ? <FiCheck size={17} /> : <FiCopy size={17} />}
                      </button>}
                    </div>
                  </td>
                  <td>{room.title}{room.source_room_id && <small style={{ display:'block' }}>Từ phòng dự án #{room.source_room_id}</small>}{room.expires_at && <small style={{ display:'block' }}>{new Date(room.expires_at) <= new Date() ? 'Hết hạn: ' : 'Hiển thị đến: '}{new Date(room.expires_at).toLocaleDateString(locale)}</small>}{!room.expires_at && <small style={{ display:'block' }}>Tin cũ chưa đặt thời hạn</small>}</td>
                  {adminMode && <td><strong>{room.owner?.name || '—'}</strong><br /><small>{room.owner?.email}</small></td>}
                  <td>{room.address}, {room.city}</td>
                  <td>{Number(room.price).toLocaleString(locale)} {t('myRooms.currencyShort')}</td>
                  <td>
                    {room.expires_at && new Date(room.expires_at) <= new Date() && <span className="badge badge-danger">Tin hết hạn</span>}
                    {room.inventory_access_revoked&&<span className="badge badge-danger">Đã thu hồi quyền kho</span>}
                    {room.reservation?.status === 'pending' && <span className="badge badge-warning">Đang giữ phòng</span>}
                    <button type="button" role="switch" aria-checked={room.status === 'available'}
                      aria-label={`${t('myRooms.quickStatus')}: ${room.title}`}
                      title={t(room.status === 'available' ? 'myRooms.deactivateRoom' : 'myRooms.activateRoom')}
                      disabled={!canManageRoom(room) || updatingRoomIds[room.id]}
                      aria-busy={Boolean(updatingRoomIds[room.id])}
                      onClick={() => toggleRoomStatus(room)}
                      className={`badge room-quick-status ${
                      room.status === 'available' ? 'badge-success' : 
                      room.status === 'rented' ? 'badge-warning' : 'badge-danger'
                    }`}>
                      {room.status === 'available' ? <FiToggleRight size={20} aria-hidden="true" /> : <FiToggleLeft size={20} aria-hidden="true" />}
                      <span>{updatingRoomIds[room.id] ? t('myRooms.updatingStatus') : adminMode ? (statusLabels[room.status] || room.status) : room.status === 'available'
                        ? t('myRooms.statusAvailable')
                        : room.status === 'rented'
                          ? t('myRooms.statusRented')
                          : t('myRooms.statusMaintenance')}</span>
                    </button>
                  </td>
                  <td>
                    <div className="action-group">
                      <button className="btn btn-neutral" disabled={renewing} onClick={() => prepareRenewal(room)}>Gia hạn</button>
                      <Link to={`/rooms/${room.id}`} className="btn btn-primary" aria-label={t('myRooms.viewAria', { title: room.title })}>
                        {t('myRooms.view')}
                      </Link>
                      <Link to={`/edit-room/${room.id}`} className="btn btn-warning" aria-label={t('myRooms.editAria', { title: room.title })}>
                        {t('myRooms.edit')}
                      </Link>
                      <button 
                        onClick={() => setRoomToDelete(room.id)}
                        className="btn btn-danger"
                        aria-label={t('myRooms.deleteAria', { title: room.title })}
                      >
                        {t('myRooms.delete')}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        </>
      )}
      <ConfirmDialog open={Boolean(renewal)} title="Gia hạn tin đăng" message={renewal ? `Gia hạn “${renewal.room.title}” thêm ${renewal.plan.duration_days} ngày với phí ${Number(renewal.plan.price).toLocaleString('vi-VN')} đ. Số dư ví: ${Number(renewal.plan.balance).toLocaleString('vi-VN')} đ. Gia hạn không tự mở lại phòng đã thuê hoặc bảo trì.` : ''} confirmText={renewing ? 'Đang gia hạn…' : 'Xác nhận gia hạn'} cancelText="Đóng" onConfirm={renew} onCancel={() => { if (!renewing) setRenewal(null); }} />
      <ConfirmDialog open={Boolean(releasing)} title="Kết thúc thuê phòng" message={`Xác nhận khách đã trả phòng “${releasing?.title || ''}” và mở lại phòng? Lịch sử phiếu cọc và hoa hồng đã ghi nhận được giữ nguyên.`} confirmText={renewing ? 'Đang xử lý…' : 'Kết thúc thuê'} cancelText="Đóng" onConfirm={releaseRental} onCancel={() => { if (!renewing) setReleasing(null); }} />
      {adminMode && !loading && !fetchError && pagination.last_page > 1 && <div className="pagination" aria-label={en ? 'Room pages' : 'Phân trang phòng'}>
        <button className="btn" type="button" disabled={page <= 1} onClick={() => setPage((value) => value - 1)}>{en ? 'Previous' : 'Trước'}</button>
        <span>{en ? 'Page' : 'Trang'} {page} / {pagination.last_page}</span>
        <button className="btn" type="button" disabled={page >= pagination.last_page} onClick={() => setPage((value) => value + 1)}>{en ? 'Next' : 'Sau'}</button>
      </div>}

      <ConfirmDialog
        open={Boolean(roomToDelete)}
        title={t('myRooms.confirmDeleteTitle') || 'Xác nhận xóa'}
        message={t('myRooms.confirmDelete')}
        confirmText={t('myRooms.delete')}
        cancelText={t('common.cancel') || 'Hủy'}
        onConfirm={handleDelete}
        onCancel={() => setRoomToDelete(null)}
      />
    </div>
  );
};

export default MyRooms;
